import { checkAuthentication } from "./apis";

const AUTH_ERROR_STATUSES = [302, 401, 403];
const STAMP_INPUT_TYPES = {
  file: "file",
  hash: "content_id",
  text: "text",
  portfolio: "portfolio",
};

export const createSubmit = async (
  objectHash,
  selectedFile,
  setLoading,
  setError,
  handleUpdateHashWorker,
  selectedOptionCollection,
  handleSelectedOptionCollection,
  verificationMethod,
  setFileName,
  setIsAuthenticated = undefined,
) => {
  // Check authentication status before making request
  const authStatus = await checkAuthentication(setIsAuthenticated);

  // If user is not authenticated, don't proceed
  if (authStatus === false) {
    return;
  }

  // If authentication check returned null (error), continue with request (let server handle it)

  setLoading(true);
  const csrfToken = document.querySelector("[name=csrfmiddlewaretoken]").value;
  const formData = new FormData();
  formData.append("object-hash", objectHash);
  if (selectedOptionCollection?.cid) {
    formData.append("collection-hash", selectedOptionCollection.cid);
  }

  if (selectedFile) {
    formData.append("file", selectedFile);
  }

  const stampInputType = STAMP_INPUT_TYPES[verificationMethod];
  if (stampInputType) {
    formData.append("stamp-input-type", stampInputType);
  }

  // Enable save file storage if stamp from Text or Portfolio
  if (verificationMethod === "text" || verificationMethod === "portfolio") {
    formData.append("upload-enabled", true);
  }

  try {
    const response = await fetch("/stamp/create-stamp/", {
      method: "POST",
      headers: {
        "X-CSRFToken": csrfToken,
      },
      body: formData,
    });

    // Check if user is not authenticated (redirect to login or 401/403)
    if (AUTH_ERROR_STATUSES.includes(response.status)) {
      setLoading(false);
      setError({
        error: {
          user_message:
            "Authentication failed. Your session may have expired. Please log in again.",
          technical_details: {
            message: `Unauthorized access (HTTP ${response.status})`,
            timestamp: new Date().toISOString(),
            error_type: "Authentication Error",
          },
        },
      });
      handleSelectedOptionCollection("");
      return;
    }

    // Check Content-Type before parsing JSON
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      setLoading(false);
      // Determine if this is likely an authentication issue
      const isAuthError =
        AUTH_ERROR_STATUSES.includes(response.status) ||
        (response.status === 200 &&
          contentType &&
          contentType.includes("text/html"));

      // Create a more user-friendly technical message
      let technicalMessage;
      if (response.status === 302) {
        technicalMessage = "Server redirected to login page (HTTP 302)";
      } else if (AUTH_ERROR_STATUSES.includes(response.status)) {
        technicalMessage = `Unauthorized access (HTTP ${response.status})`;
      } else if (contentType && contentType.includes("text/html")) {
        technicalMessage = `Expected JSON response but received HTML page (HTTP ${response.status}). This usually indicates an authentication issue.`;
      } else {
        technicalMessage = `Unexpected response format: ${
          contentType || "unknown"
        } (HTTP ${response.status})`;
      }

      setError({
        error: {
          user_message: isAuthError
            ? "Authentication failed. Your session may have expired. Please log in again."
            : "Unexpected server response. Please try again or contact support.",
          technical_details: {
            message: technicalMessage,
            timestamp: new Date().toISOString(),
            error_type: isAuthError ? "Authentication Error" : "Server Error",
          },
        },
      });
      handleSelectedOptionCollection("");
      return;
    }

    const data = await response.json();

    if (!response.ok || data.error) {
      // Server error - pass the full response object
      setError(data);
      setLoading(false);
    } else {
      // Success
      handleUpdateHashWorker(objectHash, null, true);
      setFileName(data?.fileObject?.fileName);
    }
    handleSelectedOptionCollection("");
  } catch (error) {
    // Network or parsing error
    setLoading(false);

    // Check if it's a JSON parsing error (likely due to HTML response from redirect)
    if (error.message && error.message.includes("JSON")) {
      setError({
        error: {
          user_message:
            "Authentication failed. Your session may have expired. Please log in again.",
          technical_details: {
            message: error.message,
            timestamp: new Date().toISOString(),
            error_type: "Authentication Error",
          },
        },
      });
    } else {
      // Actual network error
      setError({
        error: {
          user_message:
            "Unable to connect to the server. Please check your internet connection and try again.",
          technical_details: {
            message: error.message,
            timestamp: new Date().toISOString(),
            error_type: "Network Error",
          },
        },
      });
    }
    handleSelectedOptionCollection("");
  }
};
